import React from 'react';
import { motion } from 'framer-motion';
import { FaHeart, FaComment, FaShare, FaCamera, FaThumbtack, FaBookmark, FaTrash } from 'react-icons/fa';
import { postsAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import VerificationBadge from '../VerificationBadge';

const PostsTab = ({ posts, isOwnProfile, onPinPost, onDeletePost }) => (
  <div className="space-y-4">
    {posts.length > 0 ? posts.map(post => (
      <motion.div key={post.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all group">
        <p className="text-gray-700 mb-4">{post.content}</p>
        {post.media_url && <img src={post.media_url} alt="" className="w-full rounded-lg mb-4 max-h-96 object-cover" />}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-6 text-gray-500">
            <span className="flex items-center space-x-1"><FaHeart className="text-red-400" /><span>{post.likes_count}</span></span>
            <span className="flex items-center space-x-1"><FaComment className="text-[#4A90E2]" /><span>{post.comments_count}</span></span>
            <span className="flex items-center space-x-1"><FaShare className="text-green-500" /><span>{post.shares_count}</span></span>
          </div>
          {isOwnProfile && (
            <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-all">
              <button onClick={() => onPinPost(post.id)} className="text-gray-400 hover:text-[#4A90E2]" title="PinPoint"><FaThumbtack /></button>
              <button onClick={() => onDeletePost(post.id)} className="text-gray-400 hover:text-red-500" title="Delete post" data-testid={`delete-post-${post.id}`}><FaTrash /></button>
            </div>
          )}
        </div>
      </motion.div>
    )) : (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">No <span className="font-arthaus">points</span> yet</p>
      </div>
    )}
  </div>
);

const MediaTab = ({ posts }) => {
  const mediaPosts = posts.filter(p => p.media_url);
  if (mediaPosts.length === 0) {
    return <div className="text-center py-12 bg-white rounded-xl border border-gray-200"><FaCamera className="text-4xl text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No photos or videos yet</p></div>;
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
      {mediaPosts.map(post => (
        <motion.div key={post.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} whileHover={{ scale: 1.02 }} className="aspect-square rounded-xl overflow-hidden cursor-pointer relative group">
          {post.media_type?.startsWith('video') ? (
            <><video src={post.media_url} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/30 flex items-center justify-center"><div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center"><span className="text-gray-900 text-lg ml-1">▶</span></div></div></>
          ) : (
            <img src={post.media_url} alt="" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex items-center space-x-4 text-white">
              <span className="flex items-center space-x-1"><FaHeart /><span>{post.likes_count}</span></span>
              <span className="flex items-center space-x-1"><FaComment /><span>{post.comments_count}</span></span>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

const UserListTab = ({ users, emptyText }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {users.length > 0 ? users.map(u => (
      <Link key={u.id} to={`/profile/${u.id}`} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center space-x-4 hover:shadow-md transition-all">
        <img src={u.avatar_url || `https://ui-avatars.com/api/?name=${u.full_name}`} alt={u.full_name} className="w-14 h-14 rounded-full object-cover" />
        <div className="flex-1">
          <div className="flex items-center space-x-1">
            <span className="font-semibold text-gray-900">{u.full_name}</span>
            {u.is_verified && <VerificationBadge badgeType={u.verification_badge} size="sm" />}
          </div>
          <span className="text-gray-500 text-sm">@{u.username}</span>
        </div>
      </Link>
    )) : (
      <div className="col-span-2 text-center py-12 bg-white rounded-xl border border-gray-200"><p className="text-gray-500">{emptyText}</p></div>
    )}
  </div>
);

const BookmarksTab = ({ bookmarks }) => (
  <div className="space-y-4">
    {bookmarks.length > 0 ? bookmarks.map(post => (
      <motion.div key={post.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all">
        <div className="flex items-center space-x-3 mb-3">
          <Link to={`/profile/${post.username}`}><img src={post.avatar_url || `https://ui-avatars.com/api/?name=${post.full_name}`} alt={post.full_name} className="w-10 h-10 rounded-full hover:ring-2 hover:ring-[#4A90E2]/40 transition-all" /></Link>
          <div><Link to={`/profile/${post.username}`} className="hover:underline"><span className="font-semibold text-gray-900">{post.full_name}</span></Link><span className="text-gray-500 text-sm ml-2">@{post.username}</span></div>
        </div>
        <p className="text-gray-700">{post.content}</p>
      </motion.div>
    )) : (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200"><FaBookmark className="text-4xl text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No bookmarks yet</p></div>
    )}
  </div>
);

const LikesTab = () => (
  <div className="text-center py-12 bg-white rounded-xl border border-gray-200"><FaHeart className="text-4xl text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Liked posts will appear here</p></div>
);

const ProfileTabContent = ({ activeTab, posts, bookmarks, followers, following, isOwnProfile, onPinPost, onDeletePost }) => {
  switch (activeTab) {
    case 'posts': return <PostsTab posts={posts} isOwnProfile={isOwnProfile} onPinPost={onPinPost} onDeletePost={onDeletePost} />;
    case 'media': return <MediaTab posts={posts} />;
    case 'likes': return <LikesTab />;
    case 'followers': return <UserListTab users={followers} emptyText="No followers yet" />;
    case 'following': return <UserListTab users={following} emptyText="Not following anyone yet" />;
    case 'bookmarks': return isOwnProfile ? <BookmarksTab bookmarks={bookmarks} /> : null;
    default: return null;
  }
};

export default ProfileTabContent;
